import React from 'react';
import moment from 'moment';

// 3rd party
import CalendarIcon from "calcite-ui-icons-react/CalendarIcon";
import PinIcon from "calcite-ui-icons-react/PinIcon";

// aiim.datasets
import FieldNames from '../../../../../aiim/datasets/FieldNames';

// aiim.util
import { getAttributeValue } from '../../../../../aiim/util/aiimUtil';
import * as itemUtil from '../../../../../aiim/util/itemUtil';
import { escSqlQuote } from '../../../../../aiim/util/selectionUtil';

// components.main.InfoPanel
import * as Actions from "../../../InfoPanel/Actions";
import InfoPanelHeader from '../../../InfoPanel/InfoPanelHeader';
import ShareDropdown from '../../../InfoPanel/ShareDropdown';
import { generateShareUrl } from '../../../InfoPanel/shareUtil';

// components.main.More.Actions.BookWorkspace.WorkspaceReservation 
import { formatDate } from './WorkspaceReservation/workspaceReservationUtil';
import { queryCheckInOut } from "./WorkspaceReservation/OfficeHotelingInterface";
import { getBookingSystem } from "./WorkspaceReservation/workspaceReservationUtil";
import ItemReference from '../../../../../aiim/base/ItemReference';

// components.util
import * as component from '../../../../util/component';

// context
import Context from '../../../../../context/Context';
import Topic from '../../../../../context/Topic';
import CheckInOut from './CheckInOut';

// util
import { stringFormatter } from '../../../../../util/formatUtil';

const CSS = {
  main: "i-content",
  infopanelContent: "i-infopanel-content",
  featureContent: "i-infopanel-content-feature",
  actionBar: "i-actionbar",
  actionBarContainer: "i-actionbar-container",
  reservationInfo: "i-reservation-info",
  reservationDate: "i-reservation-date",
  reservationLocation: "i-reservation-location",
  reservationLocationMain: "i-reservation-location-main",
  reservationLocationSub: "i-reservation-location-sub"
};

export default class ReservationDetails extends React.Component {
  constructor(props) {
    super(props);
    this.state = component.newState({
      shareUrl: null,
      item : this.props.item.getFeature(),
      unitItem: null
    })
  }

  componentDidMount() {
    this._generateUnitUrl();

    component.own([
      Topic.subscribe(Topic.ReferenceLayerUpdated, () => {
        component.refresh(this);
      }),
      Topic.subscribe(Topic.ShowSearchResult, (params)=>{
        let attributes;
        if(params && params.searchResult && params.searchResult.feature) attributes = params.searchResult.feature;
        this.setState({
          item :  attributes
        })
        component.refresh(this);
      }),
      Topic.subscribe(Topic.UpdateReservationDetails, (params) => {
        const objectId = params.objectId;
        const bookingSystem = getBookingSystem();
        queryCheckInOut(bookingSystem, objectId).then((result)=>{
          if(result && result.features && result.features[0] && result.features[0].attributes) {
            this.setState({
              item : result.features[0]
            })
          }
        })
        component.refresh(this);
      })
    ]);
  }

  async _generateUnitUrl() {
    const reservation = this._getFeature();
    const units = Context.getInstance().aiim.datasets.units;
    const unitId = getAttributeValue(reservation.attributes, FieldNames.UNIT_ID);
    if (!unitId) {
      return;
    }

    // Find the matching unit
    const where = `${units.uidField} = '${escSqlQuote(unitId)}'`;
    try {
      const result = await units.query(where);
      const features = result && result.features
      const unitFeature = features && features.length === 1 && features[0];

      // Make item
      const item = new ItemReference();
      item._fromFeature(units.getSource().key, unitFeature);
      this.setState({
        unitItem: item,
        shareUrl: generateShareUrl(item)
      });
    } catch(e) {
      console.error("Unable to query unit", e);
    }
  }

  _getFeature() {
    const { item } = this.props;
    return item.getFeature();
  }
  
  _getSource() {
    const { item } = this.props;
    return item.getSource();
  }

  _getCaption() {
    const i18n = Context.getInstance().i18n;
    const reservation = this._getFeature();
    const attributes = reservation.attributes;
    const title = getAttributeValue(attributes, FieldNames.TITLE);

    const unitName = getAttributeValue(attributes, FieldNames.UNIT_NAME);
    const defaultCaption = i18n.more.bookWorkspace.emailSubject.replace("{workspaceName}", unitName);

    return title || defaultCaption;
  }

  renderActions() {
    const { unitItem, shareUrl, item } = this.state;
    const actions = [];

    const feature = unitItem && unitItem.getFeature();
    const source = unitItem && unitItem.getSource();
    const hasGeometry = itemUtil.hasGeometry(source, feature);

    if (unitItem && unitItem.isValid()) {
      
      actions.push(
        <li key="checkInOut">
          {<CheckInOut action="checkInOut" item={item}/>}
        </li>
      )

      actions.push(
        <li key="trash">
          {<CheckInOut action="cancel" item={item}/>}
        </li>
      )

      // Directions, if network configured
      if (Context.getInstance().config.networkServiceUrl) {
        actions.push(
          <li key="directions">
            <Actions.Directions 
              item={unitItem}
              disabled={!hasGeometry}  
            />
          </li>
        );
      }

      // Save to My Places
      // actions.push(
      //   <li key="saveFavorite">
      //     <Actions.SaveFavorite 
      //       item={item} 
      //       disabled={!hasGeometry}
      //     />
      //   </li>
      // );

      // Share Link (copy or QR code)
      if (shareUrl) {
        actions.push(
          <li key="share">
            <ShareDropdown 
              shareUrl={this.state.shareUrl} 
              caption={this._getCaption()}
            />
          </li>
        );
      }
    }

    if (actions.length === 0) {
      return null;
    }

    return (
      <div className={CSS.actionBarContainer}>
        <ul className={CSS.actionBar}>
          {actions}
        </ul>
      </div>
    );
  }

  renderReservationInfo() {
    const i18n = Context.getInstance().i18n;
    const reservations = Context.getInstance().aiim.datasets.reservations;
    const levels = Context.getInstance().aiim.datasets.levels;
    const reservation = this._getFeature();
    const attributes = reservation.attributes;

    // Date info
    const startTime = getAttributeValue(attributes, FieldNames.START_TIME);
    const endTime = getAttributeValue(attributes, FieldNames.END_TIME);
    const isAllDay = getAttributeValue(attributes, FieldNames.ALL_DAY);
    const mStartTime = moment(startTime);
    const mEndTime = moment(endTime);
    const formattedDate = formatDate(mStartTime, mEndTime, isAllDay);

    // Location info
    const floorField = reservations.levelIdField;
    const unitName = getAttributeValue(attributes, FieldNames.UNIT_NAME);
    const levelId = getAttributeValue(attributes, floorField);
    const levelData = levels.getLevelData(levelId);
    const levelName = levelData.levelName;
    const facilityName = levelData.facilityName;
    const template = i18n.more.bookWorkspace.levelDescription;
    const floorAwareInfo = stringFormatter(template, { 
      level: levelName, 
      facility: facilityName 
    });

    return (
      <div className={CSS.reservationInfo}>
        <span className={CSS.reservationDate}>
          <CalendarIcon />
          {formattedDate}
        </span>
        <span className={CSS.reservationLocation}>
          <PinIcon />
          <span className={CSS.reservationLocationMain}>
            {unitName}
            <span className={CSS.reservationLocationSub}>
              {floorAwareInfo}
            </span>
          </span>
        </span>
      </div>
    );
  }

  renderContent() {
    return (
      <div key="i-infopanel-content" className={CSS.infopanelContent}>
        <div key="i-feature-content" className={CSS.featureContent}>
          {this.renderActions()}
          {this.renderReservationInfo()}
        </div>
      </div>
    );
  }

  renderHeader() {
    const caption = this._getCaption();
    return <InfoPanelHeader caption={caption} infoPanelKey={this.props.infoPanelKey} />
  }

  render() {
    return (
      <div className={CSS.main}>
        {this.renderHeader()}
        {this.renderContent()}
      </div>
    );
  }
}